import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { FiChevronRight, FiInfo } from "react-icons/fi";
import { Sparkline } from "../../activity-logs/components/primitives";
import {
  Section, Widget, WidgetGrid, tokens,
} from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import {
  HeaderLink, Notice, PeriodMap, PeriodStats, Tag,
} from "../../creditos/styles";
import {
  formatCompact, formatCurrency, formatMonth, formatPercent,
} from "../../creditos/dashboardData";
import { hairline, surface, tokens as T } from "../../../styles/designTokens";
import { MoneyParts } from "../caixa/CashDashboard";
import { CardRow, Figure } from "../dashboard/ReformaDashboard";
import MonthlyChart from "./MonthlyChart";
import {
  TABLE_GROUPS, TAXES, buildMonthly, scopeOf,
} from "./dadosData";
import { AnimatedMoney as MoneyValue, Num } from "../components/Motion";

/**
 * Dados mensais: o que o SPED registrou, mês a mês. Um mês selecionado no gráfico, no
 * mapa, nas leituras ou na tabela vale para a aba inteira. A cor segue a aba Caixa:
 * azul é débito, verde é crédito, vermelho é o que sai do caixa.
 */

const DEBIT = IOS.blue;
const CREDIT = IOS.green;
const PAY = IOS.red;
const label2 = ({ theme }) => tokens.label2(theme);
const label3 = ({ theme }) => tokens.label3(theme);
const dot = (color) => <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, opacity: 1 }} />;
const spark = { width: 80, height: 24, points: 60 };

const whole = (v) => {
  const n = Math.round(Math.abs(v || 0)).toLocaleString("pt-BR");
  return v < 0 ? `−${n}` : n;
};
const compactSigned = (v) => (v < 0 ? `−${formatCompact(Math.abs(v))}` : formatCompact(v));
// Texto curto para caber na célula do mapa
const cellValue = (v) => {
  const abs = Math.abs(v);
  const sign = v < 0 ? "−" : "";
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mi`;
  return `${sign}${Math.round(abs / 1e3).toLocaleString("pt-BR")} mil`;
};

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`;

const Legend = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 0.76em;
`;

const BigFigure = styled.div`
  margin-top: 14px;
  .big { font-size: 1.72em; font-weight: 600; letter-spacing: -0.04em; line-height: 1.05; white-space: nowrap; }
  .big .sign { margin-right: 1px; font-weight: 500; color: ${CREDIT}; }
  .big .cur { margin-right: 3px; font-size: 0.5em; font-weight: 500; letter-spacing: 0; opacity: 0.5; }
  .big .unit { margin-left: 3px; font-size: 0.5em; font-weight: 500; letter-spacing: 0; opacity: 0.5; }
  .cap { margin-top: 4px; font-size: 0.76em; color: ${label2}; }
`;

const TaxLines = styled.div`
  flex: 1;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;

  .tax { padding: 11px 0; border-top: 1px solid ${({ theme }) => hairline(theme)}; }
  .head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
  .name { font-size: 0.86em; font-weight: 500; letter-spacing: -0.012em; }
  .net { font-size: 0.94em; font-weight: 600; letter-spacing: -0.025em; white-space: nowrap; }
  .pair { display: grid; grid-template-columns: minmax(0, 1fr) 76px; align-items: center; gap: 4px 10px; margin-top: 7px; }
  .track { height: 4px; }
  .track i { display: block; height: 100%; min-width: 3px; border-radius: 2px; opacity: 0.85; }
  .val { text-align: right; white-space: nowrap; font-size: 0.72em; color: ${label2}; }
`;

const MapScroll = styled.div`
  flex: 1;
  display: flex;
  overflow-x: auto;
  margin-top: 16px;
  margin-bottom: 14px;
  padding: 2px;
  scrollbar-width: thin;
`;

// As linhas crescem com o card, para o mapa acompanhar a altura do card ao lado
const TaxMap = styled(PeriodMap)`
  flex: 1;
  grid-template-columns: 52px repeat(var(--months), minmax(0, 1fr));
  grid-template-rows: auto repeat(3, minmax(36px, 1fr));
  min-width: calc(52px + var(--months) * 48px);

  .year { font-weight: 500; color: ${({ theme }) => theme.text}; font-size: 1.05em; }
  .head { white-space: nowrap; }
  .head.on { color: ${({ theme }) => theme.text}; font-weight: 600; }
  .cell { height: auto; min-height: 36px; color: inherit; font-family: inherit; }
  .cell .fill { background: var(--tone); opacity: var(--level); }
  .cell.on { box-shadow: inset 0 0 0 1.5px ${({ theme }) => tokens.label2(theme)}; }
  .cell:focus-visible { outline: none; box-shadow: 0 0 0 2px ${T.brandRing}; }
  .cell span.txt {
    position: relative;
    z-index: 1;
    font-size: 0.9em;
    font-weight: 500;
    letter-spacing: -0.03em;
    white-space: nowrap;
    color: ${({ theme }) => theme.text};
  }
`;

const StaticRow = styled(CardRow).attrs({ as: "div" })`
  cursor: default;
  grid-template-columns: 132px minmax(0, 1fr);
  &:hover { background: transparent; }
`;

const TableScroll = styled.div`
  margin: 14px -18px -16px;
  overflow-x: auto;
  scrollbar-width: thin;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1120px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.82em;
  font-variant-numeric: tabular-nums;

  th, td { padding: 0 10px; text-align: right; white-space: nowrap; }
  th { font-weight: 400; color: ${label3}; }
  thead tr:first-child th { padding-top: 4px; padding-bottom: 8px; vertical-align: bottom; }
  thead tr:last-child th { padding-bottom: 8px; border-bottom: 1px solid ${({ theme }) => hairline(theme, true)}; font-size: 0.9em; }
  .group { text-align: left; font-size: 1em; font-weight: 600; letter-spacing: -0.012em; color: ${({ theme }) => theme.text}; }
  .group small { display: block; margin-top: 2px; font-size: 0.82em; font-weight: 400; letter-spacing: 0; color: ${label3}; }
  .start { border-left: 1px solid ${({ theme }) => hairline(theme)}; }

  tbody td { height: 38px; border-bottom: 1px solid ${({ theme }) => hairline(theme)}; color: ${label2}; transition: background 140ms ease; }
  tbody td.strong { font-weight: 600; letter-spacing: -0.015em; color: ${({ theme }) => theme.text}; }
  tbody td.credit { color: ${CREDIT}; }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr.row { cursor: pointer; }
  tbody tr.row:hover td { background: ${({ theme }) => surface(theme, 1)}; }
  tbody tr.on td { background: ${({ theme }) => surface(theme, 2)}; color: ${({ theme }) => theme.text}; }
  tbody tr.on td.credit { color: ${CREDIT}; }
  tbody tr.gap td { color: ${label3}; cursor: default; }

  tfoot td { height: 44px; border-top: 1px solid ${({ theme }) => hairline(theme, true)}; font-weight: 600; letter-spacing: -0.015em; color: ${({ theme }) => theme.text}; }
  tfoot td.credit { color: ${CREDIT}; }

  /* A coluna do mês fica no lugar quando a tabela rola para o lado */
  th.month, td.month, tbody tr.row:hover td.month, tbody tr.on td.month {
    position: sticky;
    left: 0;
    z-index: 1;
    width: 92px;
    padding-left: 18px;
    text-align: left;
    background: ${({ theme }) => tokens.solid(theme)};
  }

  button.pick {
    padding: 0;
    border: none;
    background: none;
    color: ${({ theme }) => theme.text};
    font: inherit;
    font-weight: 500;
    letter-spacing: -0.01em;
    cursor: pointer;
    border-radius: 4px;
  }
  tr.on button.pick { font-weight: 650; }
  button.pick:focus-visible { outline: none; box-shadow: 0 0 0 2px ${T.brandRing}; }
`;

const Footnote = styled.p`
  margin: 10px 2px 0;
  font-size: 0.76em;
  font-weight: 300;
  line-height: 1.5;
  color: ${label2};
`;

function FlowWidget({ months, selected, onSelect }) {
  return (
    <Widget $span={3} $delay={200} style={{ display: "flex", flexDirection: "column" }}>
      <TitleRow>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">Movimento do período</div>
          <div className="w-sub">Quanto saiu e entrou em notas, e o tributo que sobrou em cada mês</div>
        </div>
        <Legend>
          <Tag $color={IOS.blue}>Faturamento maior</Tag>
          <Tag $color={IOS.green}>Compras maiores</Tag>
        </Legend>
      </TitleRow>
      <MonthlyChart months={months} selected={selected} onSelect={onSelect} />
    </Widget>
  );
}

function ScopeWidget({ scope, month, onClear }) {
  return (
    <Widget $delay={250} style={{ display: "flex", flexDirection: "column" }}>
      <TitleRow>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">{month ? formatMonth(month) : "Período inteiro"}</div>
          <div className="w-sub">Débito, crédito e líquido</div>
        </div>
        {month && <HeaderLink type="button" onClick={onClear}>Ver período</HeaderLink>}
      </TitleRow>
      <BigFigure>
        <div className="big" title={formatCurrency(scope.net)}>
          {scope.net < 0 && <span className="sign">−</span>}<MoneyParts value={scope.net} />
        </div>
        <div className="cap">tributo líquido · {formatPercent(scope.netShare)} do faturamento</div>
      </BigFigure>
      <TaxLines>
        {scope.lines.map((t) => {
          const max = Math.max(t.debitValue, t.creditValue, 1);
          return (
            <div className="tax" key={t.key}>
              <div className="head">
                <span className="name">{t.label}</span>
                <span className="net" title={formatCurrency(t.netValue)} style={t.netValue < 0 ? { color: CREDIT } : undefined}><Num value={t.netValue} format={compactSigned} /></span>
              </div>
              <div className="pair">
                <span className="track"><i style={{ width: `${(t.debitValue / max) * 100}%`, background: DEBIT }} /></span>
                <span className="val" title={`${t.debitLabel}: ${formatCurrency(t.debitValue)}`}><Num value={t.debitValue} format={formatCompact} /></span>
                <span className="track"><i style={{ width: `${(t.creditValue / max) * 100}%`, background: CREDIT }} /></span>
                <span className="val" title={`${t.creditLabel}: ${formatCurrency(t.creditValue)}`}><Num value={t.creditValue} format={formatCompact} /></span>
              </div>
            </div>
          );
        })}
      </TaxLines>
    </Widget>
  );
}

function MapWidget({
  months, maxNet, total, selected, onSelect,
}) {
  return (
    <Widget $span={2} $delay={300} style={{ display: "flex", flexDirection: "column" }}>
      <TitleRow>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">Tributo líquido por mês</div>
          <div className="w-sub">Cada linha na própria escala, para o mês fora da curva aparecer</div>
        </div>
        <Legend>
          <Tag $color={PAY}>A pagar</Tag>
          <Tag $color={CREDIT}>Saldo credor</Tag>
        </Legend>
      </TitleRow>
      <MapScroll>
        <TaxMap style={{ "--months": months.length }}>
          <span />
          {months.map((m) => <span key={m.periodo} className={`head ${m.periodo === selected ? "on" : ""}`}>{formatMonth(m.periodo)}</span>)}
          {TAXES.map((t) => (
            <React.Fragment key={t.key}>
              <span className="year">{t.label}</span>
              {months.map((m) => {
                const value = m[t.net] || 0;
                return (
                  <button
                    key={m.periodo}
                    type="button"
                    className={`cell ${m.periodo === selected ? "on" : ""}`}
                    disabled={m.missing}
                    title={m.missing ? `${formatMonth(m.periodo)}: sem SPED` : `${t.label} líquido em ${formatMonth(m.periodo)}: ${formatCurrency(value)}`}
                    onClick={() => onSelect(m.periodo === selected ? null : m.periodo)}
                  >
                    {!m.missing && <span className="fill" style={{ "--tone": value < 0 ? CREDIT : PAY, "--level": 0.06 + (Math.abs(value) / maxNet[t.key]) * 0.5 }} />}
                    <span className="txt">{m.missing ? "—" : cellValue(value)}</span>
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </TaxMap>
      </MapScroll>
      <PeriodStats>
        {TAXES.map((t) => (
          <div key={t.key}>
            <span className="v" title={formatCurrency(total?.[t.net])}><Num value={total?.[t.net] || 0} format={compactSigned} /></span>
            <span className="l" title={`${t.label} líquido no período`} style={{ whiteSpace: "normal" }}>{t.label} líquido</span>
          </div>
        ))}
      </PeriodStats>
    </Widget>
  );
}

function ReadingRow({ reading: r, onSelect }) {
  let figure;
  let text;
  if (r.times !== undefined) {
    figure = <Figure times={r.times} />;
    text = <>{formatCompact(r.peak)} de tributo líquido, contra {formatCompact(r.typical)} na mediana dos meses.</>;
  } else {
    figure = <span className="figure">{r.count}<span className="unit">de {r.of}</span></span>;
    text = r.money !== undefined
      ? <>{r.sub}: {formatMonth(r.where)}, com {formatCompact(r.money)} a mais em compras.</>
      : r.sub;
  }
  return (
    <CardRow type="button" onClick={() => onSelect(r.where)} title={`Ver ${formatMonth(r.where)}`}>
      <span className="metric">{figure}<span className="cap">{r.caption}</span></span>
      <span className="text"><span className="t">{r.title}</span><span className="s">{text}</span></span>
      <FiChevronRight className="go" />
    </CardRow>
  );
}

function ReadingsWidget({ readings, coverage, onSelect }) {
  const {
    count, first, last, missing,
  } = coverage;
  const range = first === last ? formatMonth(first) : `${formatMonth(first)} a ${formatMonth(last)}`;
  return (
    <Widget $span={2} $delay={350}>
      <div className="w-title">O que chama atenção nos dados</div>
      <div className="w-sub">Calculado dos próprios registros. Clique para ver o mês</div>
      <div style={{ marginTop: 12 }}>
        {readings.map((r) => <ReadingRow key={r.key} reading={r} onSelect={onSelect} />)}
        <StaticRow>
          <span className="metric">
            <span className="figure">{count}<span className="unit">{count === 1 ? "mês" : "meses"}</span></span>
            <span className="cap">com SPED</span>
          </span>
          <span className="text">
            <span className="t">Cobertura: {range}</span>
            <span className="s">
              {missing.length > 0
                ? `Sem SPED em ${missing.map(formatMonth).join(", ")}. Esses meses aparecem zerados.`
                : "Sem meses faltando dentro do intervalo."}
            </span>
          </span>
        </StaticRow>
      </div>
    </Widget>
  );
}

function DataTable({
  months, total, sources, selected, onSelect,
}) {
  const columns = TABLE_GROUPS.flatMap((g) => g.columns.map((c, i) => ({ ...c, start: i === 0 })));
  const toneOf = (key, value) => (key.endsWith("Liquido") && value < 0 ? "credit" : "");
  return (
    <Widget $delay={400}>
      <div className="w-title">Dados mensais do SPED</div>
      <div className="w-sub">Em reais, sem centavos. Passe o mouse em um valor para ver os centavos, e em uma coluna para ver o registro de origem</div>
      <TableScroll>
        <Table>
          <thead>
            <tr>
              <th className="month" rowSpan={2} style={{ verticalAlign: "bottom", paddingBottom: 8 }}>Período</th>
              {TABLE_GROUPS.map((g) => (
                <th key={g.key} colSpan={g.columns.length} className="group start">
                  {g.label}
                  <small>{g.source}</small>
                </th>
              ))}
            </tr>
            <tr>
              {columns.map((c) => <th key={c.key} className={c.start ? "start" : ""} title={sources[c.key]}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr
                key={m.periodo}
                className={m.missing ? "gap" : `row ${m.periodo === selected ? "on" : ""}`}
                onClick={m.missing ? undefined : () => onSelect(m.periodo === selected ? null : m.periodo)}
              >
                <td className="month">
                  {m.missing
                    ? formatMonth(m.periodo)
                    : <button type="button" className="pick" aria-pressed={m.periodo === selected} onClick={(e) => { e.stopPropagation(); onSelect(m.periodo === selected ? null : m.periodo); }}>{formatMonth(m.periodo)}</button>}
                </td>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`${c.start ? "start" : ""} ${c.strong ? "strong" : ""} ${toneOf(c.key, m[c.key])}`}
                    title={m.missing ? "Sem SPED neste mês" : formatCurrency(m[c.key])}
                  >
                    {m.missing ? "—" : whole(m[c.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {total && (
            <tfoot>
              <tr>
                <td className="month">Total</td>
                {columns.map((c) => (
                  <td key={c.key} className={`${c.start ? "start" : ""} ${toneOf(c.key, total[c.key])}`} title={formatCurrency(total[c.key])}>{whole(total[c.key])}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </Table>
      </TableScroll>
    </Widget>
  );
}

export default function MonthlyDashboard({ fiscalData, companyFiltered }) {
  const data = useMemo(() => buildMonthly(fiscalData), [fiscalData]);
  const [picked, setPicked] = useState(null);
  const {
    months, total, sources, maxNet, readings, coverage,
  } = data;

  // Um mês que saiu do filtro deixa de estar selecionado
  const row = months.find((m) => m.periodo === picked && !m.missing) || null;
  const selected = row ? row.periodo : null;
  const scope = scopeOf(row || total);
  const scopeName = row ? formatMonth(row.periodo) : `${coverage.count} ${coverage.count === 1 ? "mês" : "meses"}`;
  const real = months.filter((m) => !m.missing);

  return (
    <>
      <Section $first>
        {companyFiltered && (
          <Notice style={{ marginTop: 0, marginBottom: 12 }}>
            <FiInfo />
            <span>Com empresa selecionada, PIS e COFINS seguem a apuração da matriz. Compare com cuidado com o ICMS de cada estabelecimento.</span>
          </Notice>
        )}
        <WidgetGrid>
          <Widget $delay={0}>
            <div className="w-head">{dot(IOS.blue)} Faturamento</div>
            <div className="w-value"><MoneyValue value={scope.revenue} /></div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{scopeName}</span>
              <Sparkline data={real.map((m) => m.faturamento || 0)} color={IOS.blue} {...spark} />
            </div>
          </Widget>
          <Widget $delay={50}>
            <div className="w-head">{dot(IOS.green)} Compras</div>
            <div className="w-value"><MoneyValue value={scope.purchases} /></div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{formatPercent(scope.purchasesShare)} do faturamento</span>
              <Sparkline data={real.map((m) => m.compras || 0)} color={IOS.green} {...spark} />
            </div>
          </Widget>
          <Widget $delay={100}>
            <div className="w-head">{dot(PAY)} Tributo líquido</div>
            <div className="w-value"><MoneyValue value={scope.net} /></div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>PIS, COFINS e ICMS</span>
              <Sparkline data={real.map((m) => Math.max(0, m.net))} color={PAY} {...spark} />
            </div>
          </Widget>
          <Widget $delay={150}>
            <div className="w-head">{dot(IOS.gray)} Crédito sobre débito</div>
            <div className="w-value"><Num value={scope.coverage} format={formatPercent} /></div>
            <div className="w-foot" title={`Crédito: ${formatCurrency(scope.credit)} · Débito: ${formatCurrency(scope.debit)}`}>
              {formatCompact(scope.credit)} de {formatCompact(scope.debit)}
            </div>
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={80}>
        <WidgetGrid>
          <FlowWidget months={months} selected={selected} onSelect={setPicked} />
          <ScopeWidget scope={scope} month={selected} onClear={() => setPicked(null)} />
        </WidgetGrid>
      </Section>

      <Section $delay={120}>
        <WidgetGrid>
          <MapWidget months={months} maxNet={maxNet} total={total} selected={selected} onSelect={setPicked} />
          <ReadingsWidget readings={readings} coverage={coverage} onSelect={setPicked} />
        </WidgetGrid>
      </Section>

      <Section $delay={160}>
        <DataTable months={months} total={total} sources={sources} selected={selected} onSelect={setPicked} />
        <Footnote>Dados extraídos diretamente dos registros C100 dos SPEDs ICMS/IPI e PIS/COFINS processados para este projeto.</Footnote>
      </Section>
    </>
  );
}
